/**
 *
 * <article b-tpl="slider image text" data-moonwalk-section>
   <div data-panner-container data-moonwalk="u">
     <div data-panner-next><span>&rarr;</span></div>
     <div data-panner-previous><span>&larr;</span></div>
     <div class="inner">
       <div data-panner data-panner-snap>
         <figure data-panner-item>
           <div class="asset">
             {% ref refs.pic %}
           </div>
           <div class="text" data-typo>
             {% ref refs.text %}
           </div>
         </figure>
       </div>
     </div>
     <div data-panner-indicator>
       <div data-panner-indicator-nib="{{ entries | size }}"></div>
     </div>
   </div>
 </article>
 *
 * autoplay -- set data-panner-autoplay="3000" on the panner element
 */
import {
  Dom,
  Draggable,
  gsap,
  imagesAreLoaded,
  Events,
  _defaultsDeep,
  InertiaPlugin,
} from '@brandocms/jupiter'

gsap.registerPlugin(Draggable)
gsap.registerPlugin(InertiaPlugin)

const DEFAULT_OPTIONS = {
  draggableDefaults: {
    edgeResistance: 0.65,
    dragClickables: false,
    overshootTolerance: 0,
  },
  slideDuration: 1,
  slideEase: 'power2.inOut',
  autoplay: false,
  centered: false,
  startProgress: null,
  allowContextMenu: true,
  cursor: 'move',
  triggerContainer: true,
  snap: false,
  snapCenter: false,
  loop: false,
  loopExperimental: false, // endless loop testing. Currently BROKEN.
  roll: false,
  wheel: false,
  clickToNavigate: false,
  indicator: false,
  waitForImagesLoaded: true,
  breakpoints: false,
  hideOverflow: true,
  extendedEnd: true, // if true, when snapping, the last item will be at the left side of canvas
  padContainerEnd: false, // if true, adds container width to end of panner canvas.
  // this needs a `--container-padding` css var to be set
  // (done automatically by europacss)
  elements: {
    dot: () => {
      const button = document.createElement('button')
      button.type = 'button'
      button.dataset.pannerDot = ''
      button.appendChild(document.createTextNode('•'))
      return button
    },
  },
  events: {
    onClick: (panner, e) => {
      if (panner.opts.clickToNavigate) {
        if (e.clientX > window.innerWidth / 2) {
          panner.moveNext()
          return
        }
        panner.movePrevious()
      }
    },

    clickableTest: el => {
      if (
        el.hasAttribute('data-panner-previous') ||
        el.hasAttribute('data-panner-next') ||
        el.hasAttribute('data-clickable')
      ) {
        return true
      }
      return false
    },

    onPointerLeft: (panner, e) => {
      panner.elements.container.setAttribute('data-panner-hover-left', true)
      panner.elements.container.removeAttribute('data-panner-hover-right')
    },

    onPointerRight: (panner, e) => {
      panner.elements.container.setAttribute('data-panner-hover-right', true)
      panner.elements.container.removeAttribute('data-panner-hover-left')
    },
  },
}

const getClosest = (n, values, max, min) => {
  const bigNum = 1e10
  let i = values.length
  let closest = 0
  let absDif = bigNum
  let val
  let dif

  while (i--) {
    val = values[i]
    dif = val - n
    if (dif < 0) {
      dif = -dif
    }
    if (dif < absDif && val >= min && val <= max) {
      closest = i
      absDif = dif
    }
  }
  return values[closest]
}

export default class Panner {
  constructor(app, el, opts = {}) {
    this.app = app
    this.opts = { ..._defaultsDeep(opts, DEFAULT_OPTIONS) }
    this.movingIt = false
    this.autoplayRunning = false
    this.rolling = false

    this.elements = {}
    this.elements.container = el
    this.elements.panner = Dom.find(this.elements.container, '[data-panner]')
    this.elements.items = Dom.all(this.elements.container, '[data-panner-item]')

    this.elements.items.forEach((item, index) =>
      item.setAttribute('data-panner-slide-idx', index)
    )

    this.elements.images = Dom.all(this.elements.container, '[data-panner-item] img')
    this.elements.indicator = Dom.find(this.elements.container, '[data-panner-indicator]')
    this.elements.dots = Dom.find(this.elements.container, '[data-panner-dots]')
    this.elements.previous = Dom.all(this.elements.container, '[data-panner-previous]')
    this.elements.next = Dom.all(this.elements.container, '[data-panner-next]')
    this.elements.index = Dom.find(this.elements.container, '[data-panner-index]')

    if (this.opts.triggerContainer) {
      this.opts.trigger = el
    } else {
      this.opts.trigger = this.elements.panner
    }

    this.name = this.elements.panner.getAttribute('data-panner-name') || 'unnamed'
    this.opts.name = this.elements.panner.getAttribute('data-panner-name') || 'unnamed'

    this.state = {}
    this.state.disableNext = false
    this.state.disablePrevious = true
    this.state.manuallyMoved = false
    this.state.offsets = []
    this.state.idx = 0

    this.timeline = gsap.timeline()
    this.checkSnap()
    this.checkLoop()
    this.checkIndicator()
    this.checkDirectionalButtons()
    this.checkAutoplay()
    this.checkDots()
    this.checkRoll()
    this.checkBreakpoints()
    this.checkStartProgress()

    this.setupPanner()
  }

  checkSnap() {
    this.opts.snap = this.elements.panner.hasAttribute('data-panner-snap')
    if (this.opts.snap) {
      const attr = this.elements.panner.getAttribute('data-panner-snap')
      if (attr === 'center') {
        this.opts.snapCenter = true
      }
    }
  }

  checkLoop() {
    this.opts.loop = this.elements.panner.hasAttribute('data-panner-loop')
  }

  checkRoll() {
    this.opts.roll = this.elements.panner.hasAttribute('data-panner-roll')
  }

  checkBreakpoints() {
    const attr = this.elements.panner.getAttribute('data-panner-breakpoints')
    if (attr) {
      this.opts.breakpoints = JSON.parse(attr)
    }
  }

  checkIndicator() {
    if (this.elements.indicator) {
      this.setupIndicator()
    }
  }

  checkDots() {
    if (this.elements.dots) {
      this.opts.hasDots = true
      this.setupDots()
    }
  }

  checkAutoplay() {
    const autoplayAttr = this.elements.panner.getAttribute('data-panner-autoplay')
    if (autoplayAttr !== null) {
      this.opts.autoplay = autoplayAttr
    }
  }

  checkDirectionalButtons() {
    if (this.elements.next.length > 0) {
      this.opts.hasDirectionalButtons = true
      this.setupDirections()
    }
  }

  checkStartProgress() {
    const dataProgress = this.elements.panner.getAttribute('data-panner-start-progress')
    const dataCentered = this.elements.panner.hasAttribute('data-panner-centered')

    if (dataProgress) {
      this.opts.startProgress = parseFloat(dataProgress)
    } else if (this.opts.startProgress !== null) {
      // value from JS options is used
    } else if (dataCentered) {
      this.opts.startProgress = 0.5
    } else if (this.opts.centered) {
      this.opts.startProgress = 0.5
    }
  }

  refreshBounds(waitForLoaded) {
    if (waitForLoaded) {
      imagesAreLoaded(this.elements.images, true).then(() => {
        this.doRefresh()
      })
    } else {
      this.doRefresh()
    }
  }

  setPannerMinMax(offset) {
    this.pannerMaxX = 0
    if (offset !== undefined) {
      this.pannerMinX = offset
    } else {
      this.pannerMinX =
        (this.elements.panner.scrollWidth -
          this.elements.panner.clientWidth +
          (this.opts.padContainerEnd ? this.getContainerWidth() : 0)) *
        -1
    }

    if (this.opts.loop) {
      this.setPannerWrapMinX()
    }
  }

  setPannerWrapMinX() {
    const firstSlide = Dom.find('[data-panner-slide-idx="0"]:not(.panner-slide-duplicated)')
    const firstPrependedSlide = Dom.find('.panner-slide-prepend[data-panner-slide-idx="0"]')
    this.wrapMinX = firstSlide.offsetLeft - firstPrependedSlide.offsetLeft
  }

  splitUnit(str) {
    if (!str) {
      return [0, 'px']
    }

    const string = String(str)
    const val = parseFloat(string, 10)
    const unit = string.match(/[\d.\-+]*\s*(.*)/)[1] || ''

    return [val, unit]
  }

  getContainerWidth() {
    const cw = getComputedStyle(document.documentElement)
      .getPropertyValue('--container-padding')
      .trim()
    const docWidth = document.documentElement.clientWidth
    const [cwVal, cwUnit] = this.splitUnit(cw)

    switch (cwUnit) {
      case '%':
        return (docWidth * cwVal) / 100

      case 'vw':
        return (docWidth * cwVal) / 100

      default:
        return cwVal
    }
  }

  doRefresh() {
    if (this.opts.snap || this.opts.autoplay || this.opts.extendedEnd) {
      this.refreshOffsetList()
    }
    this.setPannerMinMax()

    this.applyBounds()

    if (this.elements.indicator) {
      this.refreshIndicator()
    }
  }

  applyBounds() {
    this.panner.applyBounds({
      maxX: this.pannerMaxX,
      minX: this.pannerMinX,
    })
    this.panner.update(true)

    if (this.panner) {
      this.panner.vars.snap = this.opts.snap ? this.state.offsets : false
    }
  }

  refreshIndicator() {
    gsap.set(this.elements.indicatorNib, {
      width: this.elements.indicator.clientWidth / this.indicatorWidthFactor,
    })
    this.indicator.applyBounds(this.elements.indicator)
  }

  refreshOffsetList() {
    this.state.offsets = []

    if (!this.elements.items.length) {
      return
    }
    const leftPad = this.elements.items[0].offsetLeft

    for (let i = 0; i < this.elements.items.length; i += 1) {
      this.state.offsets.push(-this.elements.items[i].offsetLeft + leftPad)
    }

    if (this.panner) {
      this.panner.vars.snap = this.opts.snap ? this.state.offsets : false
    }

    if (this.opts.extendedEnd) {
      console.log('extendedEnd: TRUE - setting minX to last offset')
      this.setPannerMinMax(this.state.offsets[this.elements.items.length - 1])
    } else {
      console.log('extendedEnd: FALSE - using natural scroll boundary')
      this.setPannerMinMax()
    }
  }

  setupPanner() {
    if (this.opts.hideOverflow) {
      gsap.set(this.elements.container, {
        overflowX: 'hidden',
        overflowY: 'hidden',
      })
    }
    const that = this

    if (this.opts.wheel && !this.app.featureTests.results.ios) {
      this.setupMousewheel()
    }

    let snap
    let liveSnap
    let onThrowUpdate
    let onThrowComplete
    let onMove = function (x) {
      if (that.autoplayRunning) {
        that.stopAutoplay()
      }
    }

    if (this.opts.loop) {
      this.setupLoop()
      this.setPannerWrapMinX()
      if (this.opts.loopExperimental) {
        liveSnap = x => {
          return gsap.utils.wrap(0, this.wrapMinX * -1, x)
        }
      }
    }

    if (this.opts.snap) {
      if (!this.state.offsets.length) {
        this.refreshOffsetList()
      }
      snap = this.state.offsets
    } else {
      this.setPannerMinMax()
    }

    if (this.elements.indicator) {
      onThrowUpdate = function () {
        const percent =
          Math.round((Math.min(Math.max(this.x / this.minX, 0), 1) + Number.EPSILON) * 1000) /
          1000
        that.moveIndicatorToPercent(percent)
      }
    }

    if (this.elements.indicator) {
      onMove = function () {
        if (that.autoplayRunning) {
          that.stopAutoplay()
        }
        const percent =
          Math.round((Math.min(Math.max(this.x / this.minX, 0), 1) + Number.EPSILON) * 1000) /
          1000
        that.moveIndicatorToPercent(percent)
      }
    }

    if (this.opts.hasDirectionalButtons || this.opts.hasDots || this.opts.snap) {
      onThrowComplete = function () {
        // Find the closest slide to the final resting position.
        let closestIdx = -1
        let smallestDist = Infinity
        that.state.offsets.forEach((offset, i) => {
          const dist = Math.abs(offset - this.x)
          if (dist < smallestDist) {
            smallestDist = dist
            closestIdx = i
          }
        })

        if (closestIdx !== -1) {
          const newIdx = closestIdx
          if (that.opts.loop) {
            const slideElement = that.elements.items[newIdx]
            const originalIndex = parseInt(slideElement.getAttribute('data-panner-slide-idx'), 10)
            that.setIdx(originalIndex)
          } else {
            that.setIdx(newIdx)
          }
        }

        // Update arrow states and nav display
        that.state.disablePrevious = this.x >= that.pannerMaxX
        that.state.disableNext = this.x <= that.pannerMinX
        that.updateNav()
      }
    }

    if (this.opts.autoplay) {
      this.setupAutoplay()
    }

    if (this.opts.roll) {
      this.setupRoll()
    }

    const draggable = Draggable.create(this.elements.panner, {
      bounds: { maxX: this.pannerMaxX, minX: this.pannerMinX },
      ...this.opts.draggableDefaults,
      type: 'x',
      inertia: true,
      autoScroll: false,
      cursor: this.opts.cursor,
      zIndexBoost: false,
      trigger: this.opts.trigger,
      allowContextMenu: this.opts.allowContextMenu,
      clickableTest: e => {
        if (that.opts.breakpoints && !that.opts.breakpoints.includes(that.app.breakpoint)) {
          return this.opts.events.clickableTest
        }
        return false
      },
      onClick: e => this.opts.events.onClick(this, e),
      onPress: function (e) {
        if (that.opts.breakpoints && !that.opts.breakpoints.includes(that.app.breakpoint)) {
          this.endDrag(e)
        }
      },
      onDragEnd: function () {
        if (!that.opts.snap) {
          return
        }

        const destinationX = getClosest(this.endX, that.state.offsets, this.maxX, this.minX)

        let closestIdx = -1
        let smallestDist = Infinity
        that.state.offsets.forEach((offset, i) => {
          const dist = Math.abs(offset - destinationX)
          if (dist < smallestDist) {
            smallestDist = dist
            closestIdx = i
          }
        })

        if (closestIdx !== -1) {
          const newIdx = closestIdx
          if (that.opts.loop) {
            const slideElement = that.elements.items[newIdx]
            const originalIndex = parseInt(slideElement.getAttribute('data-panner-slide-idx'), 10)
            that.setIdx(originalIndex)
          } else {
            that.setIdx(newIdx)
          }
          that.updateNav()
        }
      },
      snap,
      liveSnap,
      onMove,
      onThrowUpdate,
      onThrowComplete,
    })

    // eslint-disable-next-line prefer-destructuring
    this.panner = draggable[0]

    this.updateNav()

    if (this.opts.startProgress !== null) {
      const startX = this.pannerMinX * this.opts.startProgress
      if (this.opts.snap && this.state.offsets.length > 0) {
        let closestIdx = 0
        let smallestDist = Infinity

        if (this.opts.loop) {
          const loopStartOffsetIndex = this.state.loopedItems
          const loopEndOffsetIndex = this.state.loopedItems * 2
          for (let i = loopStartOffsetIndex; i < loopEndOffsetIndex; i++) {
            const offset = this.state.offsets[i]
            const dist = Math.abs(offset - startX)
            if (dist < smallestDist) {
              smallestDist = dist
              closestIdx = i - loopStartOffsetIndex
            }
          }
        } else {
          this.state.offsets.forEach((offset, i) => {
            const dist = Math.abs(offset - startX)
            if (dist < smallestDist) {
              smallestDist = dist
              closestIdx = i
            }
          })
        }

        this.moveIdx(closestIdx)
        this.updateNav()
        if (this.elements.indicator) {
          const percent = this.panner.x / this.pannerMinX || 0
          this.moveIndicatorToPercent(percent, 0)
        }
        this.panner.update(true)
      } else {
        gsap.set(this.elements.panner, { x: startX })
        this.panner.update(true)
        this.state.disableNext = this.panner.x <= this.panner.minX
        this.state.disablePrevious = this.panner.x >= this.panner.maxX
        this.updateNav()
      }
    }

    window.addEventListener(Events.APPLICATION_RESIZE, this.onApplicationResize.bind(this))
    window.addEventListener(Events.APPLICATION_REVEALED, this.onApplicationRevealed.bind(this))

    if (this.opts.trackHover) {
      this.elements.panner.addEventListener('mousemove', event => {
        this.onMouseMove(event)
      })
    }

    // Handle focus events to ensure focused items are visible
    this.elements.panner.addEventListener('focusin', event => {
      this.onFocusIn(event)
    })
  }

  setupRoll() {
    const ROLL_SPEED = 80
    const duration = 1133 / ROLL_SPEED
    gsap.to(this.elements.panner, {
      x: -1133,
      duration: duration,
      ease: 'none',
    })
  }

  setupMousewheel() {
    this.elements.container.addEventListener('wheel', e => {
      if (e.deltaY !== 0) {
        return
      }
      e.preventDefault()
      gsap.set(this.elements.panner, { x: `-=${e.deltaX}` })
      this.applyBounds()
      if (this.elements.indicator) {
        const percent =
          Math.round(
            (Math.min(Math.max(this.panner.x / this.pannerMinX, 0), 1) + Number.EPSILON) * 1000
          ) / 1000
        this.moveIndicatorToPercent(percent)
      }
    })
  }

  setupLoop() {
    this.createLoop()
    if (this.app.lazyload) {
      console.error('(!) YOU MUST INITIALIZE A LOOPING SLIDER **BEFORE** LazyLoad')
    }
  }

  setupAutoplay() {
    // only start autoplay when in viewport
    const observer = new IntersectionObserver(
      (entries, self) => {
        entries.forEach(entry => {
          if (entry.intersectionRatio > 0) {
            this.startAutoplay()
            self.unobserve(entry.target)
          }
        })
      },
      {
        rootMargin: '-15% 0px',
        threshold: 0.0,
      }
    )

    observer.observe(this.elements.panner)
  }

  startAutoplay() {
    if (!this.opts.autoplay) {
      return
    }

    const autoplayTimer = this.opts.autoplay ? parseInt(this.opts.autoplay) : 3000

    if (!this.autoplayRunning) {
      this.autoplayID = setInterval(() => {
        if (this.state.idx >= this.elements.items.length - 1) {
          this.gotoIdx(0)
        } else {
          this.moveNext()
        }
      }, autoplayTimer)
      this.autoplayRunning = true
    }
  }

  stopAutoplay() {
    if (this.autoplayID) {
      clearInterval(this.autoplayID)
      this.autoplayRunning = false
    }
  }

  createLoop() {
    this.state.loopedItems = this.elements.items.length

    const prependSlides = []
    const appendSlides = []

    Array.from(this.elements.items).forEach((el, index) => {
      if (index < this.state.loopedItems) {
        appendSlides.push(el)
      }
      if (
        index < this.elements.items.length &&
        index >= this.elements.items.length - this.state.loopedItems
      ) {
        prependSlides.push(el)
      }
      el.setAttribute('data-panner-slide-idx', index)
    })
    for (let i = 0; i < appendSlides.length; i += 1) {
      this.elements.panner
        .appendChild(appendSlides[i].cloneNode(true))
        .classList.add('panner-slide-duplicated', 'panner-slide-append')
    }
    for (let i = prependSlides.length - 1; i >= 0; i -= 1) {
      this.elements.panner
        .insertBefore(prependSlides[i].cloneNode(true), this.elements.panner.firstChild)
        .classList.add('panner-slide-duplicated', 'panner-slide-prepend')
    }

    this.updateSlides()

    // move to the original slide
    this.refreshOffsetList()
    this.moveIdx(this.state.idx)
  }

  /**
   * Set `items` and `images` according to DOM
   */
  updateSlides() {
    this.elements.items = Dom.all(this.elements.container, '[data-panner-item]')
    this.elements.images = Dom.all(this.elements.container, '[data-panner-item] img')
  }

  onApplicationResize() {
    this.refreshBounds(false)
  }

  onApplicationRevealed() {
    this.refreshBounds(this.opts.waitForImagesLoaded)
  }

  onMouseMove(e) {
    const moveTarget = this.elements.container
    const moveTargetWidth = moveTarget.offsetWidth
    const xCoordInmoveTarget = e.clientX - moveTarget.getBoundingClientRect().left
    if (moveTargetWidth / 2 > xCoordInmoveTarget) {
      if (this.pointerDirection === 'left') {
        return
      }
      this.pointerDirection = 'left'
      this.opts.events.onPointerLeft(this, e)
    } else {
      if (this.pointerDirection === 'right') {
        return
      }
      this.pointerDirection = 'right'
      this.opts.events.onPointerRight(this, e)
    }
  }

  onFocusIn(e) {
    // Only respond to keyboard focus, not mouse/touch focus
    // This prevents the panner from animating during drag interactions
    if (!e.target.matches(':focus-visible')) {
      return
    }

    // Find which panner item contains the focused element
    const focusedItem = e.target.closest('[data-panner-item]')

    if (focusedItem) {
      // Get the index of the focused item
      const itemIndex = parseInt(focusedItem.getAttribute('data-panner-slide-idx'))

      // Navigate to that item if it's not already active
      if (!isNaN(itemIndex)) {
        // Ensure offsets are initialized
        if (!this.state.offsets || this.state.offsets.length === 0) {
          this.refreshOffsetList()
        }

        const targetX = this.state.offsets[itemIndex]
        const currentX = this.panner.x

        // Check if we need to move to the target position
        // Allow for small floating point differences
        if (Math.abs(currentX - targetX) > 0.5) {
          // Directly animate to the target position even if index matches
          this.timeline
            .to(
              this.elements.panner,
              {
                x: targetX,
                duration: this.opts.slideDuration,
                ease: this.opts.slideEase,
              },
              '>'
            )
            .call(() => {
              this.panner.update()
              this.setIdx(itemIndex)
              this.updateNav()
            })
        } else if (this.state.idx !== itemIndex) {
          // Position is correct but index needs updating
          this.setIdx(itemIndex)
          this.updateNav()
        }
      }
    }
  }

  movePannerToPercent(percent) {
    const newX = this.pannerMinX * percent
    gsap.to(this.elements.panner, { x: newX, ease: 'power3' })
  }

  moveIndicatorToPercent(percent, duration = 0.001, ease = 'none') {
    if (this.elements.indicator) {
      const newX = this.indicator.maxX * percent
      gsap.to(this.elements.indicatorNib, { duration, x: newX, ease })
    }
  }

  setupDots() {
    const count = this.elements.items.length
    for (let idx = 0; idx < count; idx += 1) {
      let dotElement = this.opts.elements.dot()
      if (idx === 0) {
        dotElement.setAttribute('data-panner-dot-active', '')
      }
      dotElement.addEventListener('click', e => {
        if (this.autoplayRunning) {
          this.stopAutoplay()
        }
        this.gotoIdx(idx)
      })
      this.elements.dots.appendChild(dotElement)
    }
  }

  setupIndicator() {
    this.elements.indicatorNib = Dom.find(this.elements.container, '[data-panner-indicator-nib]')
    this.indicatorWidthFactor = parseInt(
      this.elements.indicatorNib.getAttribute('data-panner-indicator-nib')
    )

    gsap.set(this.elements.indicatorNib, {
      width: this.elements.indicator.clientWidth / this.indicatorWidthFactor,
    })
    const that = this

    const draggable = Draggable.create(this.elements.indicatorNib, {
      type: 'x',
      bounds: this.elements.indicator,
      onDrag() {
        const percent =
          Math.round((Math.min(Math.max(this.x / this.maxX, 0), 1) + Number.EPSILON) * 100) / 100
        that.movePannerToPercent(percent)
      },
    })

    // eslint-disable-next-line prefer-destructuring
    this.indicator = draggable[0]
  }

  setupDirections() {
    Array.from(this.elements.next).forEach(el =>
      el.addEventListener('click', () => {
        if (this.autoplayRunning) {
          this.stopAutoplay()
        }
        this.moveNext()
      })
    )
    Array.from(this.elements.previous).forEach(el =>
      el.addEventListener('click', () => {
        if (this.autoplayRunning) {
          this.stopAutoplay()
        }
        this.movePrevious()
      })
    )
    this.updateNav()
  }

  updateNav() {
    this.updateArrows()
    this.updateDots()
    this.updateIndex()
  }

  updateArrows() {
    if (this.state.disableNext) {
      if (this.elements.next.length > 0) {
        Array.from(this.elements.next).forEach(el => Dom.addClass(el, 'disabled'))
      }
    } else if (this.elements.next.length > 0) {
      Array.from(this.elements.next).forEach(el => Dom.removeClass(el, 'disabled'))
    }

    if (this.state.disablePrevious) {
      if (this.elements.previous.length > 0) {
        Array.from(this.elements.previous).forEach(el => Dom.addClass(el, 'disabled'))
      }
    } else if (this.elements.previous.length > 0) {
      Array.from(this.elements.previous).forEach(el => Dom.removeClass(el, 'disabled'))
    }
  }

  updateDots() {
    if (this.elements.dots) {
      const currentDot = Dom.find(this.elements.dots, '[data-panner-dot-active]')
      if (currentDot) {
        currentDot.removeAttribute('data-panner-dot-active')
      }
      if (this.elements.dots.children.length) {
        const newDot = this.elements.dots.children[this.state.idx]
        newDot.setAttribute('data-panner-dot-active', '')
      }
    }
  }

  updateIndex() {
    if (this.elements.index) {
      const newIndex = this.opts.loop
        ? (this.state.idx % this.state.loopedItems) + 1
        : this.state.idx + 1
      this.elements.index.textContent = newIndex
    }
  }

  movePrevious(e) {
    if (e) {
      e.preventDefault()
      e.stopImmediatePropagation()
    }

    if (this.state.idx === 0) {
      return
    }

    if (this.state.disablePrevious) {
      return
    }

    const targetIdx = this.state.idx - 1
    const targetX = this.state.offsets[targetIdx]

    this.setIdx(targetIdx)

    this.timeline
      .to(
        this.elements.panner,
        {
          x: targetX,
          duration: this.opts.slideDuration,
          ease: this.opts.slideEase,
        },
        '>'
      )
      .call(
        () => {
          const percent =
            Math.round(
              (Math.min(Math.max(targetX / this.pannerMinX, 0), 1) + Number.EPSILON) * 1000
            ) / 1000
          this.moveIndicatorToPercent(percent, this.opts.slideDuration, this.opts.slideEase)
        },
        null,
        '<'
      )
      .call(() => {
        this.panner.update()
        this.updateNav()
      })
  }

  moveNext(e) {
    if (e) {
      e.preventDefault()
      e.stopImmediatePropagation()
    }

    if (this.state.idx >= this.elements.items.length - 1) {
      return
    }

    if (this.state.disableNext) {
      return
    }

    const targetIdx = this.state.idx + 1
    const targetX = this.state.offsets[targetIdx]

    this.setIdx(targetIdx)

    this.timeline
      .to(
        this.elements.panner,
        {
          x: targetX,
          duration: this.opts.slideDuration,
          ease: this.opts.slideEase,
        },
        '>'
      )
      .call(
        () => {
          const percent =
            Math.round(
              (Math.min(Math.max(targetX / this.pannerMinX, 0), 1) + Number.EPSILON) * 1000
            ) / 1000
          this.moveIndicatorToPercent(percent, this.opts.slideDuration, this.opts.slideEase)
        },
        null,
        '<'
      )
      .call(() => {
        this.panner.update()
        this.updateNav()
      })
  }

  gotoIdx(idx) {
    if (this.state.idx === idx) {
      return
    }

    if (idx > this.elements.items.length - 1 || idx < 0) {
      return
    }

    const targetX = this.state.offsets[idx]

    this.setIdx(idx)

    this.timeline
      .to(
        this.elements.panner,
        {
          x: targetX,
          duration: this.opts.slideDuration,
          ease: this.opts.slideEase,
        },
        '>'
      )
      .call(
        () => {
          const percent =
            Math.round(
              (Math.min(Math.max(targetX / this.pannerMinX, 0), 1) + Number.EPSILON) * 1000
            ) / 1000
          this.moveIndicatorToPercent(percent, this.opts.slideDuration, this.opts.slideEase)
        },
        null,
        '<'
      )
      .call(() => {
        this.panner.update()
        this.updateNav()
      })
  }

  setIdx(idx) {
    this.state.idx = idx

    this.state.disableNext = false
    this.state.disablePrevious = false

    if (this.state.idx === 0 && !this.opts.loop) {
      this.state.disablePrevious = true
    }

    if (this.state.idx >= this.elements.items.length - 1 && !this.opts.loop) {
      this.state.disableNext = true
    }
  }

  moveIdx(idx) {
    let x
    this.setIdx(idx)
    if (this.opts.loop) {
      x = this.state.offsets[idx + this.state.loopedItems]
    } else {
      x = this.state.offsets[idx]
    }
    gsap.set(this.elements.panner, { x })
  }
}
